import React, {
	forwardRef,
	memo,
	useCallback,
	useImperativeHandle,
	useMemo
} from 'react';
import PropTypes from 'prop-types';

import useForm from '../../../../hooks/useForm';

import DynamicForm from '../../DynamicForm';

const DynamicFormModule = forwardRef( (
	{
		admin,
		instructions,
		moduleData,
		sections,
		setModuleErrors,
		ui,
		validate
	},
	ref
) => {
	const fieldTypes = useMemo(
		() => ui.reduce(
			(
				acc,
				{
					id,
					type
				}
			) => ( {
				...acc,
				[ id ]: type
			} ),
			{}
		),
		[ ui ]
	);

	const toFormData = useCallback( data => data.reduce( (
		acc,
		{
			questionId,
			questionOptionId,
			value
		}
	) => {
		switch ( fieldTypes[ questionId ] ) {
			case 6: // select
			case 10: // radio
				return {
					...acc,
					[ questionId ]: questionOptionId
				};

			default:
				return {
					...acc,
					[ questionId ]: value
				};
		}
	}, {} ), [ fieldTypes ] );

	const {
		formData,
		hasChanged,
		updateField
	} = useForm( toFormData( JSON.parse( moduleData.data ) ) );

	const toData = useCallback(
		data => JSON.parse( moduleData.data ).length
			? JSON.parse( moduleData.data ).map( ( {
				questionId,
				...rest
			} ) => {
				switch ( fieldTypes[ questionId ] ) {
					case 6: // select
					case 10: // radio;
						return {
							...rest,
							questionId,
							questionOptionId: data[ questionId ]
						};

					default: // everything else
						return {
							...rest,
							questionId,
							value: data[ questionId ]
						};
				}
			} )
			: Object.entries( data ).map( ( [
				questionId,
				value
			] ) => {
				switch ( fieldTypes[ questionId ] ) {
					case 6: // select
					case 10: // radio
						return {
							id: 0,
							active: false,
							questionId: Number( questionId ),
							questionOptionId: value,
							value: ''
						};

					default: // everything else
						return {
							id: 0,
							active: false,
							questionId: Number( questionId ),
							questionOptionId: 0,
							value
						};
				}
			} ),
		[
			fieldTypes,
			moduleData.data
		]
	);

	const updateModuleData = useCallback( () => {
		const data = JSON.stringify( toData( formData ) );

		return hasChanged
			? {
				...moduleData,
				data
			}
			: null;
	}, [
		formData,
		hasChanged,
		moduleData,
		toData
	] );

	useImperativeHandle( ref, () => ( { updateModuleData } ), [ updateModuleData ] );

	return <DynamicForm
		admin={ admin }
		formData={ formData }
		instructions={ instructions }
		sections={ sections }
		setModuleErrors={ setModuleErrors }
		ui={ ui }
		updateField={ updateField }
		validate={ validate }
	/>;
} );

DynamicFormModule.displayName = 'DynamicFormModule';

DynamicFormModule.propTypes = {
	admin: PropTypes.bool,
	instructions: PropTypes.string,
	moduleData: PropTypes.object.isRequired,
	sections: PropTypes.array.isRequired,
	setModuleErrors: PropTypes.func,
	ui: PropTypes.array.isRequired,
	validate: PropTypes.bool
};

export default memo( DynamicFormModule );
